import { useEffect, useState } from "react";
import { useMsal } from "@azure/msal-react";
import {
    DetailsList, DetailsListLayoutMode, Selection, SelectionMode, IColumn, ITag, DialogFooter, Text, Stack,
    Dialog, DialogType, DefaultButton, PrimaryButton, TooltipHost, Link, Spinner, SpinnerSize, ActionButton,
    IDetailsRowProps, DetailsRow, DetailsRowCheck, MessageBar, MessageBarType
} from "@fluentui/react";

import styles from "./DocumentsDetailList.module.css";

import { handleError } from "../../toaster";
import { getToken } from "../../authConfig";
import { getGroupNames, getUserNames, PermissionsGroup, PermissionsUser } from "../../api";
import { GroupPicker } from "../GroupPicker";
import { TagPickerInline } from '../TagPicker'
import { UserPicker } from "../UserPicker/UserPicker";

export interface IDocument {
    key: string;
    name: string;
    file_path: string;
    folder_name: string;
    user_id: string;
    group_permissions: string[];
    user_permissions: string[];
    tags: string[];
    value: string;
    iconUrl: string;
    fileType: string;
    state: string;
    state_description: string;
    upload_timestamp: string;
    modified_timestamp: string;
    can_edit: boolean;
};

interface Props {
    items: IDocument[];
    onFilesSorted: (items: IDocument[]) => void;
    onFileDelete: (items: IDocument[]) => void;
    onSaveTags: (items: IDocument[]) => void;
    onSavePermissions: (items: IDocument[]) => void;
    isDeleting: boolean;
    isAdmin: boolean;
    useDarkMode: boolean;
};

export const DocumentsDetailList = ({
    items,
    onFilesSorted,
    onFileDelete,
    onSaveTags,
    onSavePermissions,
    isDeleting,
    isAdmin,
    useDarkMode }
    : Props) => {
    const client = useMsal().instance;
    const [showWarning, setShowWarning] = useState(false);
    const [isDeleteDialogVisible, setIsDeleteDialogVisible] = useState(false);
    const [isEditTagsDialogVisible, setIsEditTagsDialogVisible] = useState(false);
    const [isPermissionDialogVisible, setIsPermissionDialogVisible] = useState(false);
    const [itemsToEdit, setItemsToEdit] = useState<IDocument[]>([]);
    const [canEditItems, setCanEditItems] = useState(false);
    const [canDeleteItems, setCanDeleteItems] = useState(false);
    const [editedTags, setEditedTags] = useState<ITag[]>([]);
    const [selectedGroups, setSelectedGroups] = useState<PermissionsGroup[]>([]);
    const [selectedUsers, setSelectedUsers] = useState<PermissionsUser[]>([]);

    // Item selection
    const [selectedItemCount, setSelectedItemCount] = useState(0);
    const [selectedItems, setSelectedItems] = useState<IDocument[]>([]);
    const [selection] = useState(new Selection({
        canSelectItem: (item, index) => {
            // return index !== undefined ? items[index].can_edit : false;
            const document = items.find(doc => doc.key === item.key);
            return document ? document.can_edit : false;
        },
        onSelectionChanged: () => {
            const selectionItems = selection.getSelection() as IDocument[];
            const selectionItemsCount = selection.getSelectedCount();

            setSelectedItems(selectionItems);
            setSelectedItemCount(selectionItemsCount);

            if (selectionItemsCount === 0) {
                setCanEditItems(false);
                setCanDeleteItems(false);
            } else {
                setCanEditItems(selectionItems.every(item => item.state === 'Complete' && item.can_edit));
                setCanDeleteItems(selectionItems
                    .every(item =>
                        (item.state === 'Complete' || item.state === "Error")
                        && item.can_edit
                    ));
            }
        },
    }));

    const openPermissionDialog = async () => {
        try {
            setIsPermissionDialogVisible(true);

            let existingGroupNames: PermissionsGroup[] = [];
            let existingUserNames: PermissionsUser[] = [];
            // Check if only one item is selected, then fetch the existing permissions names
            if (selectedItemCount === 1) {
                const item = selectedItems[0];
                if (item.group_permissions && item.group_permissions.length > 0) {
                    // If there are already group permissions defined
                    // then fetch the group names
                    const token = client ? await getToken(client) : undefined;
                    existingGroupNames = await getGroupNames(token, item.group_permissions);
                }
                if (item.user_permissions && item.user_permissions.length > 0) {
                    // If there are already user permissions defined
                    // then fetch the user names
                    const token = client ? await getToken(client) : undefined;
                    existingUserNames = await getUserNames(token, item.user_permissions);
                }
            }

            setSelectedGroups(selectedItemCount === 1 ? existingGroupNames : []);
            setSelectedUsers(existingUserNames);
            setItemsToEdit(selectedItems);
        } catch (error) {
            handleError(error, "An unexpected error occurred while fetching group data");
        }
    };

    const closePermissionDialog = () => {
        setIsPermissionDialogVisible(false);
        setShowWarning(false);
        setSelectedGroups([]);
        setSelectedUsers([]);
        setItemsToEdit([]);
    };

    const handleSavePermissions = async () => {
        if (selectedGroups.length === 0 && selectedUsers.length === 0) {
            if (showWarning) {
                // Proceed with saving as the user has already been warned
                savePermissions();
                setShowWarning(false);
            } else {
                // Show warning message
                setShowWarning(true);
            }
        } else {
            // Proceed with saving as there are selected groups or users
            savePermissions();
        }
    };

    const savePermissions = async () => {
        if (itemsToEdit && itemsToEdit.length > 0) {
            try {
                const selectedGroupIds = selectedGroups.map(group => group.id);
                const selectedUserIds = selectedUsers.map(user => user.id);
                const updatedItems = itemsToEdit.map(item => ({
                    ...item,
                    group_permissions: selectedGroupIds,
                    user_permissions: selectedUserIds,
                }));
                onSavePermissions(updatedItems);
            } catch (error) {
                handleError(error, "Error saving permissions");
            } finally {
                // Close the dialog
                closePermissionDialog()
                // Clear current selection
                selection.setAllSelected(false);
            }
        }
    };

    const openDeleteDialog = () => {
        setItemsToEdit(selectedItems);
        setIsDeleteDialogVisible(true);
    };

    const handleDeleteFile = async () => {
        if (itemsToEdit && itemsToEdit.length > 0 && canDeleteItems) {
            try {
                onFileDelete(itemsToEdit);
            } catch (error) {
                handleError(error, "Error deleting file(s)");
            } finally {
                // Close the dialog
                setIsDeleteDialogVisible(false);
                setItemsToEdit([]);
                // Clear current selection
                selection.setAllSelected(false);
            }
        }
    };

    const openEditTagsDialog = () => {
        try {
            // Check if only one item is selected, then set the existing tags
            if (selectedItemCount === 1) {
                const item = selectedItems[0];
                const tagsAsITags = item.tags.map((tag) => ({ key: tag, name: tag }));
                setEditedTags(tagsAsITags);
            };

            setItemsToEdit(selectedItems);
            setIsEditTagsDialogVisible(true);
        } catch (error) {
            handleError(error, "An unexpected error occurred while fetching group data");
        }
    };

    const handleSaveTags = async () => {
        if (itemsToEdit && itemsToEdit.length > 0) {
            try {
                // Convert the tags from ITag[] back to string[]
                const newTags = editedTags.map(tag => tag.key as string);
                const updatedItems = itemsToEdit.map(item => ({
                    ...item,
                    tags: newTags,
                }));
                onSaveTags(updatedItems);

            } catch (error) {
                handleError(error, "Error updating tags");
            } finally {
                setIsEditTagsDialogVisible(false);
                setItemsToEdit([]);
                setEditedTags([]);
                // Clear current selection
                selection.setAllSelected(false);
            }
        }
    };

    const onColumnClick = (ev: React.MouseEvent<HTMLElement>, column: IColumn): void => {
        const newColumns: IColumn[] = columns.slice();
        const currColumn: IColumn = newColumns.filter(currCol => column.key === currCol.key)[0];
        newColumns.forEach((newCol: IColumn) => {
            if (newCol === currColumn) {
                currColumn.isSortedDescending = !currColumn.isSortedDescending;
                currColumn.isSorted = true;
            } else {
                newCol.isSorted = false;
                newCol.isSortedDescending = true;
            }
        });

        const newItems: IDocument[] = copyAndSort(items, currColumn.fieldName!, currColumn.isSortedDescending);

        onFilesSorted(newItems);
        setColumns(newColumns);
    };

    function copyAndSort<T>(items: T[], columnKey: string, isSortedDescending?: boolean): T[] {
        const key = columnKey as keyof T;
        return items.slice(0).sort((a: T, b: T) => ((isSortedDescending ? a[key] < b[key] : a[key] > b[key]) ? 1 : -1));
    };

    function viewFile(item: any): string {
        return `${window.location.origin}/#/ViewDocument?documentName=${encodeURIComponent(item.file_path)}`;
    };

    function renderTagsColumn(item: IDocument): JSX.Element {
        const tagsString = item.tags?.join(", ") || '';

        if (tagsString.length > 25) {
            // Display a truncated version of the tags with a tooltip for the full content
            const truncatedTags = `${tagsString.substring(0, 20)}...`;
            return (
                <TooltipHost content={tagsString}>
                    <span>{truncatedTags}</span>
                </TooltipHost>
            );
        }

        // If the text is not too long, display it without truncation or tooltip
        return <span>{tagsString}</span>;
    };

    const [columns, setColumns] = useState<IColumn[]>([
        {
            key: 'column1',
            name: 'File Type',
            className: styles.fileIconCell,
            iconClassName: styles.fileIconHeaderIcon,
            ariaLabel: 'Column operations for File type, Press to sort on File type',
            iconName: 'Page',
            isIconOnly: true,
            fieldName: 'name',
            minWidth: 16,
            maxWidth: 16,
            onColumnClick: onColumnClick,
            onRender: (item: IDocument) => (
                <TooltipHost content={`${item.fileType} file`}>
                    <img src={item.iconUrl} className={styles.fileIconImg} />
                </TooltipHost>
            ),
        },
        {
            key: 'column2',
            name: 'Name',
            fieldName: 'name',
            minWidth: 400,
            maxWidth: 500,
            isRowHeader: true,
            isResizable: true,
            isSorted: false,
            isSortedDescending: false,
            ariaLabel: 'Click to sort by file name',
            sortAscendingAriaLabel: 'Sorted A to Z',
            sortDescendingAriaLabel: 'Sorted Z to A',
            onColumnClick: onColumnClick,
            data: 'string',
            onRender: (item: IDocument) => (
                <TooltipHost
                    content={(
                        <div>
                            <strong>File Name:</strong> {item.name}<br />
                            <strong>Folder:</strong> {item.folder_name}<br />
                            <strong>Type:</strong> {item.fileType}<br />
                            Click to view file
                        </div>
                    )}>
                    <Link href={viewFile(item)} target="_blank">{item.name}</Link>
                </TooltipHost>
            ),
            isPadded: true,
        },
        {
            key: 'column3',
            name: 'Folder',
            fieldName: 'folder_name',
            minWidth: 50,
            maxWidth: 200,
            isRowHeader: true,
            isResizable: true,
            isSorted: false,
            isSortedDescending: false,
            ariaLabel: 'Click to sort by folder name',
            sortAscendingAriaLabel: 'Sorted A to Z',
            sortDescendingAriaLabel: 'Sorted Z to A',
            onColumnClick: onColumnClick,
            onRender: (item: IDocument) => (
                <TooltipHost content={`${item.folder_name} `}>
                    <span>{item.folder_name}</span>
                </TooltipHost>
            ),
            data: 'string',
            isPadded: true,
        },
        {
            key: 'column4',
            name: 'Tags',
            fieldName: 'tags',
            minWidth: 100,
            maxWidth: 150,
            isRowHeader: true,
            isResizable: true,
            isSorted: false,
            isSortedDescending: false,
            ariaLabel: 'Tags',
            data: 'string',
            onRender: renderTagsColumn,
            isPadded: true,
        },
        {
            key: 'column5',
            name: 'State',
            fieldName: 'state',
            minWidth: 60,
            maxWidth: 60,
            isResizable: true,
            isSorted: false,
            isSortedDescending: false,
            ariaLabel: 'Click to sort by state',
            onColumnClick: onColumnClick,
            data: 'string',
            onRender: (item: IDocument) => (
                <TooltipHost content={`${item.state_description} `}>
                    <span>{item.state}</span>
                </TooltipHost>
            ),
            isPadded: true,
        },
        {
            key: 'column6',
            name: 'Submitted On',
            fieldName: 'upload_timestamp',
            minWidth: 120,
            maxWidth: 120,
            isResizable: true,
            isSorted: false,
            isSortedDescending: false,
            isCollapsible: true,
            ariaLabel: 'Click to sort by submitted date',
            data: 'string',
            onColumnClick: onColumnClick,
            onRender: (item: IDocument) => (
                <TooltipHost content={`${item.upload_timestamp} `}>
                    <span>{item.upload_timestamp}</span>
                </TooltipHost>
            ),
            isPadded: true,
        },
        {
            key: 'column7',
            name: 'Last Updated',
            fieldName: 'modified_timestamp',
            minWidth: 120,
            maxWidth: 120,
            isResizable: true,
            isSorted: true,
            isSortedDescending: true,
            sortAscendingAriaLabel: 'Sorted Oldest to Newest',
            sortDescendingAriaLabel: 'Sorted Newest to Oldest',
            isCollapsible: true,
            ariaLabel: 'Click to sort by last updated date',
            data: 'number',
            onColumnClick: onColumnClick,
            onRender: (item: IDocument) => (
                <TooltipHost content={`${item.modified_timestamp} `}>
                    <span>{item.modified_timestamp}</span>
                </TooltipHost>
            ),
        },
    ]);

    const onRenderRow = (rowProps: IDetailsRowProps | undefined) => {
        if (!rowProps) {
            return null;
        }

        const item = rowProps.item;

        return (
            <DetailsRow
                {...rowProps}
                onRenderCheck={(checkProps) => {
                    // Hide selection checkbox if user can't edit the item
                    return <DetailsRowCheck {...checkProps} canSelect={item.can_edit} />;
                }}
            />
        );
    };

    // Clear warning if selectedGroups or selectedUsers are changed
    useEffect(() => {
        if (selectedGroups.length > 0 || selectedUsers.length > 0) {
            setShowWarning(false);
        }
    }, [selectedGroups, selectedUsers])

    return (
        <div>
            <div className={styles.topContainer}>
                <span className={styles.buttonArea}>
                    <Text variant="smallPlus">With Selected:</Text>
                    {isAdmin ? (
                        <ActionButton
                            iconProps={{ iconName: 'Permissions' }}
                            className={useDarkMode ? styles.buttonDark : styles.button}
                            onClick={openPermissionDialog}
                            disabled={isDeleting || !canEditItems}
                            ariaLabel="Edit Permissions">
                            Edit Permissions
                        </ActionButton>
                    ) : null}
                    <ActionButton
                        iconProps={{ iconName: 'Tag' }}
                        className={useDarkMode ? styles.buttonDark : styles.button}
                        onClick={openEditTagsDialog}
                        disabled={isDeleting || !canEditItems}
                        ariaLabel="Edit Tags">
                        Edit Tags
                    </ActionButton>
                    <ActionButton
                        iconProps={{ iconName: 'Delete' }}
                        className={useDarkMode ? styles.buttonDark : styles.button}
                        onClick={openDeleteDialog}
                        disabled={isDeleting || !canDeleteItems}
                        ariaLabel="Delete">
                        Delete
                    </ActionButton>
                </span>
            </div>
            <DetailsList
                items={items}
                compact={true}
                columns={columns}
                selection={selection}
                selectionMode={SelectionMode.multiple}
                selectionPreservedOnEmptyClick={true}
                getKey={(item) => item.key}
                setKey="none"
                layoutMode={DetailsListLayoutMode.justified}
                isHeaderVisible={true}
                onRenderRow={onRenderRow}
            />
            <Dialog
                hidden={!isDeleteDialogVisible}
                onDismiss={() => setIsDeleteDialogVisible(false)}
                dialogContentProps={{
                    type: DialogType.largeHeader,
                    title: "Confirm Deletion",
                    closeButtonAriaLabel: "Close",
                    subText: `Are you sure you want to delete the selected item(s)?`,
                }}
                modalProps={{
                    isBlocking: true,
                    styles: { main: { maxWidth: 450 } },
                }}
            >
                <DialogFooter>
                    <PrimaryButton
                        iconProps={{ iconName: 'Delete' }}
                        onClick={handleDeleteFile}
                        text="Delete"
                    />
                    <DefaultButton
                        onClick={() => setIsDeleteDialogVisible(false)}
                        text="Cancel"
                    />
                </DialogFooter>
            </Dialog>
            <Dialog
                hidden={!isEditTagsDialogVisible}
                onDismiss={() => setIsEditTagsDialogVisible(false)}
                dialogContentProps={{
                    type: DialogType.largeHeader,
                    title: "Edit Tags",
                    closeButtonAriaLabel: "Close",
                }}
                modalProps={{
                    isBlocking: true,
                    styles: {
                        main: {
                            minWidth: 450,
                            maxWidth: '80%',
                        },
                    },
                }}>
                <TagPickerInline
                    allowNewTags={true}
                    preSelectedTags={editedTags}
                    onSelectedTagsChange={(newTags) => setEditedTags(newTags)}
                    useDarkMode={useDarkMode}
                />
                <DialogFooter>
                    <PrimaryButton
                        className={useDarkMode ? styles.buttonDark : styles.button}
                        iconProps={{ iconName: 'Save' }}
                        onClick={handleSaveTags}
                        text="Save"
                        disabled={!canEditItems}
                    />
                    <DefaultButton
                        className={useDarkMode ? styles.buttonDark : styles.button}
                        iconProps={{ iconName: 'Cancel' }}
                        onClick={() => setIsEditTagsDialogVisible(false)}
                        text="Close"
                    />
                </DialogFooter>
            </Dialog>
            <Dialog
                hidden={!isPermissionDialogVisible}
                onDismiss={closePermissionDialog}
                dialogContentProps={{
                    type: DialogType.largeHeader,
                    title: "Edit Permissions",
                    closeButtonAriaLabel: "Close",
                }}
                modalProps={{
                    isBlocking: true,
                    styles: {
                        main: {
                            minWidth: 450,
                            maxWidth: '80%',
                        },
                    },
                }}>
                {itemsToEdit && itemsToEdit.length > 0 ? (
                    <Stack tokens={{ childrenGap: 20 }}>
                        <GroupPicker
                            selectedGroups={selectedGroups}
                            onGroupsChange={setSelectedGroups}
                            isAdmin={isAdmin}
                            useDarkMode={useDarkMode}
                        />
                        <UserPicker
                            selectedUsers={selectedUsers}
                            onUsersChange={setSelectedUsers}
                            useDarkMode={useDarkMode}
                        />
                        {showWarning && (
                            <MessageBar messageBarType={MessageBarType.warning}>
                                No groups or users selected. All users will have access to the selected file(s).
                                Press Save again to confirm.
                            </MessageBar>
                        )}
                        <DialogFooter>
                            <PrimaryButton
                                className={useDarkMode ? styles.buttonDark : styles.button}
                                iconProps={{ iconName: 'Save' }}
                                onClick={handleSavePermissions}
                                text="Save"
                                disabled={!canEditItems || !itemsToEdit || itemsToEdit.length === 0}
                            />
                            <DefaultButton
                                className={useDarkMode ? styles.buttonDark : styles.button}
                                iconProps={{ iconName: 'Cancel' }}
                                onClick={closePermissionDialog}
                                text="Close"
                            />
                        </DialogFooter>
                    </Stack>
                ) : (
                    <Spinner size={SpinnerSize.medium} label="Loading Permissions" ariaLive="assertive" />
                )}
            </Dialog>
        </div>
    );
};